import { defineProperty as _defineProperty, objectSpread2 as _objectSpread2 } from '../_virtual/_rollupPluginBabelHelpers.mjs';
import { extractDomain, DEVELOPMENT_DOMAIN, PRODUCE_BATCH_ENDPOINT, PRODUCE_ENDPOINT, PRODUCTION_DOMAIN } from '../config/endpoints.mjs';
import { convertAllEventsToUnderscoreCase, toUnderscoreCase } from '../helpers/convert-case.mjs';
import { v4 } from '../helpers/uuid.mjs';
import { Batcher } from '../helpers/batcher.mjs';
import { MonorailRequestError, MonorailBatchProduceError, MonorailUnableToProduceError, parseServerSchemaError } from './producer-errors.mjs';

class HttpProducer {
  static withEndpoint(edgeEndpoint) {
    return new HttpProducer(extractDomain(edgeEndpoint));
  }
  constructor(edgeDomain = DEVELOPMENT_DOMAIN, keepalive = false) {
    _defineProperty(this, "batcher", new Batcher(this));
    this.edgeDomain = edgeDomain;
    this.keepalive = keepalive;
  }
  async produceBatch(monorailEvent) {
    const body = {
      events: convertAllEventsToUnderscoreCase(monorailEvent),
      metadata: toUnderscoreCase(monorailEvent.metadata)
    };
    let edgeResponse;
    try {
      edgeResponse = await fetch(this.produceBatchEndpoint(), {
        method: 'post',
        headers: getHeadersFromMetadata(monorailEvent.metadata),
        body: JSON.stringify(body),
        keepalive: this.keepalive
      });
    } catch (error) {
      throw new MonorailRequestError(error, monorailEvent.events.map(event => event.schemaId));
    }
    if (edgeResponse.status === 207) {
      const response = await edgeResponse.json();
      throw new MonorailBatchProduceError(response);
    }
    if (!edgeResponse.ok) {
      throw new MonorailUnableToProduceError({
        status: edgeResponse.status,
        message: await edgeResponse.text()
      });
    }
    return {
      status: edgeResponse.status
    };
  }
  async produce(monorailEvent) {
    try {
      const result = await this.batcher.add(monorailEvent);
      if (result) {
        return result;
      }
    } catch (error) {
      // Batcher throws a MonorailBatchProduceError if any of the batched events fails
      // This produce() call should only throw if its specific call failed
      if (error instanceof MonorailBatchProduceError) {
        var _error$response$resul;
        const matchingErrorResponse = (_error$response$resul = error.response.result) === null || _error$response$resul === undefined ? undefined : _error$response$resul.find(errorResult => parseServerSchemaError(errorResult.message) === monorailEvent.schemaId);
        if (matchingErrorResponse) {
          throw new MonorailUnableToProduceError(matchingErrorResponse);
        }
        return {
          status: 200
        };
      }
      throw error;
    }

    // Fallback to individual produce
    let convertToUnderscoreCase = true;
    if (monorailEvent && monorailEvent.options && Object.prototype.hasOwnProperty.call(monorailEvent.options, 'convertEventCase')) {
      convertToUnderscoreCase = Boolean(monorailEvent.options.convertEventCase);
    }
    let edgeResponse;
    try {
      edgeResponse = await produceHttpEvent({
        endpoint: this.produceEndpoint(),
        keepalive: this.keepalive,
        event: _objectSpread2(_objectSpread2({}, monorailEvent), {}, {
          payload: toUnderscoreCase(monorailEvent.payload, convertToUnderscoreCase)
        })
      });
    } catch (error) {
      throw new MonorailRequestError(error, [monorailEvent.schemaId]);
    }
    if (!edgeResponse) {
      throw new MonorailUnableToProduceError({
        message: 'No response from edge'
      });
    }
    if (!edgeResponse.ok) {
      throw new MonorailUnableToProduceError({
        status: edgeResponse.status,
        message: await edgeResponse.text()
      });
    }
    return {
      status: edgeResponse.status
    };
  }
  produceBatchEndpoint() {
    return this.edgeDomain + PRODUCE_BATCH_ENDPOINT;
  }
  produceEndpoint() {
    return this.edgeDomain + PRODUCE_ENDPOINT;
  }
}
function getHeadersFromMetadata(metadata) {
  /* eslint-disable @typescript-eslint/naming-convention */
  const headers = {
    'Content-Type': 'application/json; charset=utf-8',
    'X-Monorail-Edge-Event-Created-At-Ms': (metadata && metadata.eventCreatedAtMs || Date.now()).toString(),
    'X-Monorail-Edge-Event-Sent-At-Ms': Date.now().toString(),
    'X-Monorail-Edge-Client-Message-Id': (metadata && metadata.clientMessageId || v4()).toString()
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  if (metadata && metadata.userAgent) {
    headers['User-Agent'] = metadata.userAgent;
  }
  if (metadata && metadata.remoteIp) {
    headers['X-Forwarded-For'] = metadata.remoteIp;
  }
  if (metadata && metadata.deviceInstallId) {
    headers['X-Monorail-Edge-Device-Install-Id'] = metadata.deviceInstallId;
  }
  if (metadata && metadata.client) {
    headers['X-Monorail-Edge-Client'] = metadata.client;
  }
  if (metadata && metadata.clientOs) {
    headers['X-Monorail-Edge-Client-OS'] = metadata.clientOs;
  }
  return headers;
}

// Allows avoiding HttpProducer entirely by passing in snake-cased events (but we still get typechecking)
async function produceHttpEvent({
  endpoint,
  event,
  keepalive
}) {
  return fetch(endpoint !== null && endpoint !== undefined ? endpoint : PRODUCTION_DOMAIN + PRODUCE_ENDPOINT, {
    method: 'post',
    headers: getHeadersFromMetadata(event.metadata),
    /* eslint-disable @typescript-eslint/naming-convention */
    body: JSON.stringify({
      schema_id: event.schemaId,
      payload: event.payload
    }),
    /* eslint-enable @typescript-eslint/naming-convention */
    keepalive
  });
}

export { HttpProducer, produceHttpEvent };
